import { noop } from '@saaga/shared/utils/noop.util'
import { type ReactNode, useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import type { ParsedTemplateVariable } from '@/components/organisms/template-variable-editor/template-variable.interface'
import { Form } from '@/components/ui/form'
import { ScrollArea } from '@/components/ui/scroll-area'

import { PromptTemplateDrawerFooter } from './prompt-template-drawer-footer.atom'
import { PromptTemplateDrawerForm } from './prompt-template-drawer-form.molecule'
import type { PromptTemplateDrawerFormSchemaType } from './prompt-template-drawer-form.schema'
import { PromptTemplateDrawerHeader } from './prompt-template-drawer-header.atom'
import { PromptTemplateFormPreview } from './prompt-template-form-preview.molecule'

interface PromptTemplateDrawerContentProps {
  form: UseFormReturn<PromptTemplateDrawerFormSchemaType>
  isEditing: boolean
  onCancel: () => void
  onSubmit: () => void
}

/**
 * Inner content for the prompt template drawer: header, scrollable form, and footer.
 */
export const PromptTemplateDrawerContent = ({
  form,
  isEditing,
  onCancel,
  onSubmit,
}: PromptTemplateDrawerContentProps): ReactNode => {
  const [variableList, setVariableList] = useState<ParsedTemplateVariable[]>([])

  return (
    <>
      <PromptTemplateDrawerHeader isEditing={isEditing} />
      <ScrollArea className="prompt-template-drawer-scroll min-h-0 flex-1 [&>[data-slot=scroll-area-viewport]]:h-full">
        <div className="px-6 py-4">
          <Form {...form}>
            <PromptTemplateDrawerForm form={form} onVariablesChange={setVariableList} />
          </Form>
          <div className="mt-6">
            <PromptTemplateFormPreview
              variableList={variableList}
              onSubmit={noop}
              onCancel={noop}
            />
          </div>
        </div>
      </ScrollArea>
      <PromptTemplateDrawerFooter isEditing={isEditing} onCancel={onCancel} onSubmit={onSubmit} />
    </>
  )
}

PromptTemplateDrawerContent.displayName = 'PromptTemplateDrawerContent'
